import { cn } from '@/lib/utils';

export interface BrandMarkProps {
  /**
   * `gradient` — full-color mark (top bar, popup headers, sign-in).
   * `mono`     — inherits `currentColor` (dense chrome, print, 16px favicons).
   */
  variant?: 'gradient' | 'mono';
  size?: number;
  /** Renders the workspace wordmark beside the glyph. */
  withWordmark?: boolean;
  /**
   * Admin → Org → Branding. `logoUrl` replaces the glyph entirely and `appName`
   * replaces the wordmark, so a branded workspace shows ITS mark rather than
   * ours sitting beside its name. Both are optional; omitted means default.
   */
  logoUrl?: string | null;
  appName?: string;
  className?: string;
}

const GRADIENT_ID = 'cyphex-brand-gradient';
const CLIP_ID = 'cyphex-brand-disc';

const TAIL_D =
  'M78 198 C 66 210, 54 220, 44 226 C 40 228, 36 225, 37 221 C 40 208, 46 190, 54 176 Z';
const RING_D = 'M184.5 88.4 A 69 69 0 1 0 176.8 176.8';

/**
 * THE SUPPLIED ARTWORK, not a redrawing of it.
 *
 * This used to be an inline SVG someone had hand-traced from the logo, and
 * it was not a faithful copy: the ring was far too thick and swallowed the
 * disc, the three figures merged into one blob instead of keeping the
 * white gaps between them, and the gradient ran purple-heavy rather than
 * turning blue across the top right. Rendered beside the real mark the
 * difference was obvious, which is why the logo "looked wrong" everywhere
 * it appeared.
 *
 * So the raster IS the source now. The only edit to it is the removal of
 * the green verified badge — beside presence dots that read as a STATUS,
 * which is a claim the product makes about people elsewhere and a logo
 * must not appear to make. The C's arm ended underneath that badge, so a
 * small squared notch remains at its top right where pixels were never
 * drawn; reconstructing it would mean inventing the shape, which is a
 * redraw, and this is deliberately not one.
 *
 * `mono` keeps the inline silhouette below: it is a genuine one-colour
 * variant that inherits `currentColor` for dark chrome, which an <img>
 * cannot do.
 */
/** The Cyphex Teams glyph — a "C" speech bubble around three people. */
export function BrandMark({
  variant = 'gradient',
  size = 28,
  withWordmark = false,
  logoUrl = null,
  appName = 'Cyphex Teams',
  className,
}: BrandMarkProps) {
  const paint = variant === 'gradient' ? `url(#${GRADIENT_ID})` : 'currentColor';

  if (logoUrl !== null) {
    return (
      <span className={cn('inline-flex items-center gap-2', className)}>
        <img
          src={logoUrl}
          alt={appName}
          width={size}
          height={size}
          className="shrink-0 rounded-md object-contain"
        />
        {withWordmark ? (
          <span className={cn('text-xl font-bold tracking-[-0.015em]', variant === 'mono' ? 'text-current' : 'text-foreground')}>{appName}</span>
        ) : null}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {variant === 'mono' ? (
        <svg
          viewBox="0 0 256 256"
          width={size}
          height={size}
          role="img"
          aria-label={appName}
          className="shrink-0"
        >
          <defs>
            <linearGradient id={GRADIENT_ID} x1="24" y1="24" x2="232" y2="232" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#6D28D9" />
              <stop offset="0.55" stopColor="#5233E0" />
              <stop offset="1" stopColor="#2563EB" />
            </linearGradient>
            <clipPath id={CLIP_ID}>
              <circle cx="128" cy="130" r="53" />
            </clipPath>
          </defs>

          <path d={TAIL_D} fill={paint} />
          <path d={RING_D} fill="none" stroke={paint} strokeWidth="36" strokeLinecap="round" />

          <circle cx="128" cy="130" r="53" className="fill-card" />

          <g clipPath={`url(#${CLIP_ID})`} fill={paint}>
            <circle cx="97" cy="126" r="13" />
            <path d="M78 186 v-26 a19 19 0 0 1 38 0 v26 Z" />
            <circle cx="159" cy="126" r="13" />
            <path d="M140 186 v-26 a19 19 0 0 1 38 0 v26 Z" />
            <circle cx="128" cy="112" r="20" />
            <path d="M100 186 v-32 a28 28 0 0 1 56 0 v32 Z" />
          </g>

          <g className="fill-card">
            <circle cx="167.6" cy="184.5" r="6.4" />
            <circle cx="152.6" cy="192.4" r="6" />
            <circle cx="136.6" cy="197.2" r="5.6" />
          </g>
        </svg>
      ) : (
        <img
          src="/brand/cyphex-teams-mark.png"
          alt={appName}
          width={size}
          height={size}
          className="shrink-0"
          style={{ width: size, height: size }}
          draggable={false}
        />
      )}

      {withWordmark ? (
        <span className={cn('text-xl font-bold tracking-[-0.015em]', variant === 'mono' ? 'text-current' : 'text-foreground')}>{appName}</span>
      ) : null}
    </span>
  );
}
